import { isDevMode, NgModule, inject, PLATFORM_ID } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AnimationController, IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { RouteReuseStrategy } from '@angular/router';
import { TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideAppInitializer } from '@angular/core'; 

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { RouterStateTrackerService } from './services/router-state-tracker.service';
import { InterceptorService } from './services/interceptor.service';

import {
  customAlertEnter,
  customAlertLeave,
  customModalEnter,
  customModalLeave,
  customLoaderEnter,
  customLoaderLeave,
  setGlobalAnimationController,
  setGlobalRouterStateTrackerService,
} from './animations/customAnimation';

import { SharedModule } from './shared/shared.module';
import { ServiceWorkerModule } from '@angular/service-worker';
import { lastValueFrom } from 'rxjs'; 

// REQUIRED: The TranslateLoader factory function
export function setTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

/**
 * FIX: This initializer function is refactored to use the modern 'inject'
 * and now returns the Promise directly, resolving the TypeScript error.
 */

export function initializeApp(): Promise<any> {
  // Use inject() to get dependencies inside the initializer function
  const animationCtrl = inject(AnimationController);
  const routerStateTrackerService = inject(RouterStateTrackerService);
  const translate = inject(TranslateService);
  
  // Set global service references
  setGlobalAnimationController(animationCtrl);
  setGlobalRouterStateTrackerService(routerStateTrackerService);

  // 2. Load saved language/direction from Capacitor Preferences synchronously
  return import('@capacitor/preferences').then(({ Preferences }) => {
    return Preferences.get({ key: 'appSettings' }).then(result => {
      let lang = 'en';
      let dir = 'ltr';
      
      if (result.value) {
        try {
          const settings = JSON.parse(result.value);
          lang = settings.appLanguage || 'en';
          dir = settings.appDirection || 'ltr';
        } catch (e) {
          console.error('Error parsing appSettings:', e);
        }
      }
      
      // Set language and direction BEFORE Angular boots
      document.documentElement.lang = lang;
      document.documentElement.dir = dir;
      translate.setDefaultLang(lang);
      
      // Load translations
      return lastValueFrom(translate.use(lang)).catch(error => {
        console.error('Error loading initial translations:', error);
        return Promise.resolve(); 
      });
    });
  });
}


@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    SharedModule,

    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: setTranslateLoader,
        deps: [HttpClient],
      },
    }),
    AppRoutingModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    IonicModule.forRoot({
      mode: 'ios',
      animated: true,
      alertEnter: customAlertEnter,
      alertLeave: customAlertLeave,
      modalEnter: customModalEnter,
      modalLeave: customModalLeave,
      loadingEnter: customLoaderEnter,
      loadingLeave: customLoaderLeave,
      backButtonIcon: 'arrow-back',
      backButtonText: '',
      swipeBackEnabled: true,
    }),
  ],
  providers: [
    // Modern way to provide HttpClient and interceptors
    provideHttpClient(withInterceptorsFromDi()),
    
    AnimationController,
    RouterStateTrackerService,
    { provide: HTTP_INTERCEPTORS, useClass: InterceptorService, multi: true },
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    
    // Using provideAppInitializer with the corrected initializeApp function
    provideAppInitializer(initializeApp),
  ],
  bootstrap: [AppComponent],
  schemas: [],
})
export class AppModule {}
